import { useState, useMemo, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import { radioVideos } from '@/mocks/radioContent';
import type { RadioVideo, VideoCategory } from '@/mocks/radioContent';
import VideoPlayer from './components/VideoPlayer';

type PageState = 'loading' | 'error' | 'empty' | 'ready';

const FAVORITES_KEY = 'rfb_video_favorites';

const swalMixin = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

const DISPLAY_CATEGORIES: { key: VideoCategory | 'todas'; label: string; icon: string }[] = [
  { key: 'todas', label: 'Todos', icon: 'ri-apps-line' },
  { key: 'programas', label: 'Programas', icon: 'ri-tv-line' },
  { key: 'entrevistas', label: 'Entrevistas', icon: 'ri-chat-3-line' },
  { key: 'conciertos', label: 'Eventos', icon: 'ri-music-2-line' },
  { key: 'shorts', label: 'Shorts', icon: 'ri-video-line' },
  { key: 'promociones', label: 'Promociones', icon: 'ri-gift-line' },
];

function loadFavorites(): number[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function saveFavorites(ids: number[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

export default function PublicVideos() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [videos, setVideos] = useState<RadioVideo[]>([]);
  const [filterCategory, setFilterCategory] = useState<VideoCategory | 'todas'>('todas');
  const [selectedVideo, setSelectedVideo] = useState<RadioVideo | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => loadFavorites());

  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.06;
      if (shouldFail) {
        setPageState('error');
      } else if (radioVideos.length === 0) {
        setPageState('empty');
      } else {
        setVideos(radioVideos);
        setPageState('ready');
      }
    }, 800 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.04;
      if (shouldFail) {
        setPageState('error');
      } else {
        setVideos(radioVideos);
        setPageState('ready');
      }
    }, 500 + Math.random() * 500);
    return () => clearTimeout(timer);
  }, []);

  /* ─── Filtered videos ─── */
  const filteredVideos = useMemo(() => {
    if (filterCategory === 'todas') return videos;
    return videos.filter((v) => v.category === filterCategory);
  }, [videos, filterCategory]);

  /* ─── Category counts ─── */
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    videos.forEach((v) => {
      counts[v.category] = (counts[v.category] || 0) + 1;
    });
    return counts;
  }, [videos]);

  /* ─── Favorites ─── */
  const isFavorite = useCallback(
    (id: number) => favoriteIds.includes(id),
    [favoriteIds],
  );

  const handleToggleFavorite = useCallback((id: number) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id];
      saveFavorites(next);
      return next;
    });
  }, []);

  /* ─── Related videos ─── */
  const relatedVideos = useMemo(() => {
    if (!selectedVideo) return [];
    return videos
      .filter((v) => v.category === selectedVideo.category && v.id !== selectedVideo.id)
      .slice(0, 8);
  }, [selectedVideo, videos]);

  /* ─── Share ─── */
  const handleShare = useCallback((video: RadioVideo) => {
    const shareUrl = `${window.location.origin}/videos?video=${video.id}`;
    if (navigator.share) {
      navigator
        .share({ title: video.title, url: shareUrl })
        .catch(() => copyShare(shareUrl, video.title));
    } else {
      copyShare(shareUrl, video.title);
    }
  }, []);

  function copyShare(url: string, title: string) {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        swalMixin.fire({
          toast: true,
          position: 'top-end',
          title: 'Enlace copiado',
          text: 'Compartilo con quien quieras.',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });
      })
      .catch(() => {
        swalMixin.fire({
          title: 'Compartir video',
          html: `<p class="text-sm text-foreground-500 mb-3">${escapeHtml(title)}</p>
                 <input id="share-url" class="w-full px-3 py-2 rounded-lg border border-background-200 text-sm" value="${escapeHtml(url)}" readonly onclick="this.select()" />`,
          icon: 'info',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#6b7280',
        });
      });
  }

  /* ─── Loading ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <LoadingSkeleton />
      </div>
    );
  }

  /* ─── Error ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">Error cargando videos</h2>
          <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
            No se pudieron cargar los videos. Verificá tu conexión e intentá de nuevo.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line text-base"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ─── Empty ─── */
  if (pageState === 'empty') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-video-line text-foreground-300 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">Sin videos aún</h2>
          <p className="text-sm text-foreground-500 leading-relaxed">
            Todavía no hay videos publicados. Volvé pronto para ver el contenido.
          </p>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20video%20production%20studio%20setup%20with%20cinematic%20lighting%20equipment%2C%20camera%20rig%20on%20tripod%2C%20editing%20monitor%20screens%20displaying%20timeline%2C%20creative%20filmmaking%20workspace%2C%20warm%20ambient%20studio%20lighting%2C%20modern%20content%20creation%20environment%2C%20shallow%20depth%20of%20field%20focusing%20on%20camera%20lens%2C%20editorial%20video%20production%20photography&width=1600&height=900&seq=videos-hero-bg&orientation=landscape"
          alt="Videos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <i className="ri-video-line text-white text-lg"></i>
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight">Videos</h1>
          <p className="text-white/70 text-sm md:text-base mt-3 max-w-lg leading-relaxed">
            Programas completos, entrevistas exclusivas, conciertos en vivo y contenido detrás de cámara.
          </p>
          <div className="flex items-center gap-5 mt-4 text-white/60 text-xs md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-video-fill text-white/80"></i>
              {videos.length} videos
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-white/80"></i>
              {videos.reduce((s, v) => s + v.views, 0).toLocaleString()} visualizaciones
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-line text-white/80"></i>
              {favoriteIds.length} favoritos
            </span>
          </div>
        </div>
      </section>

      {/* Category filter bar */}
      <section className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-max">
            {DISPLAY_CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setFilterCategory(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  filterCategory === cat.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-600'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.label}
                {cat.key !== 'todas' && (
                  <span className={`text-[10px] ${filterCategory === cat.key ? 'text-background-50/60' : 'text-foreground-300'}`}>
                    {categoryCounts[cat.key] || 0}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-video-add-line text-foreground-300 text-xl"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-500">Sin videos en esta categoría</p>
            <p className="text-xs text-foreground-400 mt-1">Probá con otro filtro.</p>
            <button
              onClick={() => setFilterCategory('todas')}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-go-back-line"></i>
              Mostrar todos
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filteredVideos.map((video) => (
              <button
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200 cursor-pointer transition-all hover:border-background-300 text-left"
              >
                {/* Thumbnail */}
                <div className="aspect-video w-full relative overflow-hidden rounded-t-2xl">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all group-hover:scale-100 scale-75">
                      <i className="ri-play-fill text-white text-2xl ml-1"></i>
                    </div>
                  </div>
                  {/* Duration badge */}
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-[11px] font-mono">
                    {video.duration}
                  </div>
                  {/* Favorite indicator */}
                  {isFavorite(video.id) && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/35 backdrop-blur-sm flex items-center justify-center">
                      <i className="ri-heart-fill text-red-400 text-xs"></i>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3.5">
                  <h3 className="text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line text-xs"></i>
                      {video.views.toLocaleString()}
                    </span>
                    <span>{video.date}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-[10px] font-semibold">
                      {DISPLAY_CATEGORIES.find((c) => c.key === video.category)?.label || video.category}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          video={selectedVideo}
          relatedVideos={relatedVideos}
          isFavorite={isFavorite(selectedVideo.id)}
          onClose={() => setSelectedVideo(null)}
          onToggleFavorite={handleToggleFavorite}
          onShare={handleShare}
          onSelectVideo={setSelectedVideo}
        />
      )}
    </div>
  );
}

/* ─── Loading Skeleton ─── */
function LoadingSkeleton() {
  return (
    <>
      <div className="w-full h-[300px] md:h-[380px] bg-background-100 animate-pulse"></div>

      <div className="sticky top-0 z-30 bg-background-50/95 border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="w-24 h-8 rounded-full bg-background-100 animate-pulse"></div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-background-200 overflow-hidden">
              <div className="aspect-video w-full bg-background-100 animate-pulse"></div>
              <div className="p-3.5 space-y-2">
                <div className="w-3/4 h-4 rounded bg-background-100 animate-pulse"></div>
                <div className="w-1/2 h-3 rounded bg-background-100 animate-pulse"></div>
                <div className="w-20 h-5 rounded-md bg-background-100 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}